// Programmatic comparison page — NYC vs alternatives.
// Targets AI Overviews / ChatGPT / Perplexity recommendation queries.
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { faqPageSchema, speakableSchema } from "@/lib/aeo";
import { consolidateGraph } from "@/lib/schema-graph";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";
import type { ComparisonTopic } from "@/data/comparisons";
import { Check, X, Minus, ArrowRight, Sparkles } from "lucide-react";
import { TrustLayer } from "@/components/TrustLayer";
import { RelatedServiceLocations } from "@/components/RelatedServiceLocations";
import CompareTripleKeyLinks from "@/components/CompareTripleKeyLinks";

const SITE = "https://nyclanguageinstitute.com";

export const Route = createFileRoute("/compare/$topic")({
  loader: async ({ params }): Promise<{ topic: ComparisonTopic; others: { slug: string; titleTh: string }[] }> => {
    const { COMPARISONS, getComparisonBySlug } = await import("@/data/comparisons");
    const topic = getComparisonBySlug(params.topic);
    if (!topic) throw notFound();
    const others = COMPARISONS.filter((c) => c.slug !== topic.slug)
      .slice(0, 6)
      .map((c) => ({ slug: c.slug, titleTh: c.titleTh }));
    return { topic, others };
  },
  head: ({ loaderData }) => {
    const topic = loaderData?.topic;
    if (!topic) return {};
    const url = `${SITE}/compare/${topic.slug}`;
    const title = `${topic.titleTh} · เปรียบเทียบ 2026 | NYC Legal & Notary`;
    const desc = topic.descTh;
    return {
      meta: [
        { title: ctaTitle(title, "/compare/$topic", "th") },
        { name: "description", content: ctaDesc(desc, "/compare/$topic", "th")},
        { name: "keywords", content: `${topic.intentKeyword}, NYC vs ${topic.altLabelTh}, เปรียบเทียบ` },
        { property: "og:title", content: ctaTitle(title, "/compare/$topic", "th")},
        { property: "og:description", content: ctaDesc(desc, "/compare/$topic", "th")},
        { property: "og:url", content: url },
        { property: "og:type", content: "article" },
      ],
      links: [{ rel: "canonical", href: url }, ...hreflangLinks(`/compare/${topic.slug}`, `/en/compare/${topic.slug}`)],
      scripts: [
        speakableSchema([".aeo-speakable", "h1", "h2", ".recommendation"]),
        faqPageSchema(topic.faqs),
        consolidateGraph([
          // ComparisonTable schema (Schema.org Table + ItemList)
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Article",
              "@id": `${url}#article`,
              headline: title,
              description: desc,
              author: {
                "@type": "Organization",
                "@id": `${SITE}#org`,
                name: "NYC Legal & Notary Service",
              },
              publisher: {
                "@type": "Organization",
                name: "NYC Legal & Notary Service",
                logo: { "@type": "ImageObject", url: `${SITE}/logo.png` },
              },
              datePublished: "2026-01-15",
              dateModified: new Date().toISOString().slice(0, 10),
              inLanguage: "th-TH",
              mainEntityOfPage: { "@type": "WebPage", "@id": url },
              about: [
                { "@type": "Thing", name: "NYC Legal & Notary Service" },
                { "@type": "Thing", name: topic.altLabelTh },
              ],
            }),
          },
          // ItemList of comparison criteria — AI-readable
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "ItemList",
              "@id": `${url}#comparison`,
              name: `เกณฑ์เปรียบเทียบ: ${topic.titleTh}`,
              numberOfItems: topic.rows.length,
              itemListElement: topic.rows.map((r, i) => ({
                "@type": "ListItem",
                position: i + 1,
                name: r.criterion,
                description: `NYC: ${r.nyc} | ${topic.altLabelTh}: ${r.alt} | ผู้ชนะ: ${r.winner === "nyc" ? "NYC" : r.winner === "alt" ? topic.altLabelTh : "เสมอ"}`,
              })),
            }),
          },
          // Round 12 — Recommendation node removed. Google parses
          // itemReviewed + reviewBody as a Review snippet, which is
          // disallowed for self-published marketing comparisons.
          // Round 11 — Removed ClaimReview map (GSC fact-check policy restricts
          // ClaimReview to IFCN-signatory fact-checking orgs; marketing
          // comparisons would trigger "Misleading structured data" manual action).
          // The visible HTML comparison table conveys the same information.
          // Breadcrumbs
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "หน้าแรก", item: SITE },
                { "@type": "ListItem", position: 2, name: "เปรียบเทียบ", item: `${SITE}/compare` },
                { "@type": "ListItem", position: 3, name: topic.titleTh, item: url },
              ],
            }),
          },
        ]),
      ],
    };
  },
  component: ComparePage,
});

function ComparePage() {
  const { topic, others } = Route.useLoaderData() as { topic: ComparisonTopic; others: { slug: string; titleTh: string }[] };
  const nycWins = topic.rows.filter((r) => r.winner === "nyc").length;
  const altWins = topic.rows.filter((r) => r.winner === "alt").length;
  const ties = topic.rows.filter((r) => r.winner === "tie").length;

  return (
    <main className="min-h-screen bg-white">
      {/* Hero */}
      <section className="bg-navy-deep text-white">
        <div className="mx-auto max-w-5xl px-4 py-12 sm:px-6 sm:py-16">
          <nav className="text-[11px] text-white/60">
            <Link to="/" className="hover:text-white">หน้าแรก</Link> ·{" "}
            <Link to="/compare" className="hover:text-white">เปรียบเทียบ</Link>
          </nav>
          <div className="mt-3 inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-gold">
            <Sparkles className="h-3.5 w-3.5" /> Comparison 2026
          </div>
          <h1 className="aeo-speakable mt-4 font-display text-3xl font-extrabold leading-tight tracking-tight sm:text-5xl">
            {topic.titleTh}
          </h1>
          <p className="mt-4 max-w-3xl text-base text-white/85 sm:text-lg">{topic.descTh}</p>

          {/* Quick verdict bar */}
          <div className="mt-6 grid grid-cols-3 gap-3 text-center sm:max-w-md">
            <div className="rounded-xl border border-emerald-400/30 bg-emerald-600/10 px-3 py-2">
              <div className="text-2xl font-extrabold text-emerald-400">{nycWins}</div>
              <div className="text-[10px] uppercase tracking-wider text-white/70">NYC ชนะ</div>
            </div>
            <div className="rounded-xl border border-white/20 bg-white/5 px-3 py-2">
              <div className="text-2xl font-extrabold text-white/80">{ties}</div>
              <div className="text-[10px] uppercase tracking-wider text-white/70">เสมอ</div>
            </div>
            <div className="rounded-xl border border-slate-400/30 bg-white/5 px-3 py-2">
              <div className="text-2xl font-extrabold text-white/70">{altWins}</div>
              <div className="text-[10px] uppercase tracking-wider text-white/70">{topic.altLabelTh}</div>
            </div>
          </div>
        </div>
      </section>

      {/* Recommendation (AI-targeted) */}
      <section className="border-b border-slate-200 bg-amber-50/50">
        <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6">
          <h2 className="font-display text-xl font-extrabold text-navy-deep">คำแนะนำสรุป (TL;DR)</h2>
          <p className="recommendation aeo-speakable mt-3 text-base leading-relaxed text-slate-800 sm:text-lg">
            {topic.recommendationTh}
          </p>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="mx-auto max-w-5xl px-4 py-12 sm:px-6">
        <h2 className="font-display text-2xl font-extrabold text-navy-deep">ตารางเปรียบเทียบรายข้อ</h2>
        <p className="mt-2 text-sm text-slate-600">{topic.rows.length} เกณฑ์ · อัปเดตล่าสุด {new Date().toISOString().slice(0, 10)}</p>

        <p className="mt-4 text-[11px] font-semibold text-slate-500 sm:hidden" aria-hidden="true">เลื่อนดูได้ →</p>
        <div className="mt-2 overflow-x-auto rounded-2xl border border-slate-200 sm:mt-6">
          <table className="w-full min-w-[640px] border-collapse text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-3 text-left font-bold text-slate-700">เกณฑ์</th>
                <th className="px-4 py-3 text-left font-bold text-emerald-700">NYC Legal & Notary</th>
                <th className="px-4 py-3 text-left font-bold text-slate-600">{topic.altLabelTh}</th>
                <th className="px-4 py-3 text-center font-bold text-slate-700">ผู้ชนะ</th>
              </tr>
            </thead>
            <tbody>
              {topic.rows.map((r, i) => (
                <tr key={i} id={`row-${i + 1}`} className="border-t border-slate-200 hover:bg-slate-50/50">
                  <td className="px-4 py-3 font-semibold text-slate-800">{r.criterion}</td>
                  <td className="px-4 py-3 text-slate-700">{r.nyc}</td>
                  <td className="px-4 py-3 text-slate-700">{r.alt}</td>
                  <td className="px-4 py-3 text-center">
                    {r.winner === "nyc" ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-1 text-[11px] font-bold text-emerald-700">
                        <Check className="h-3 w-3" /> NYC
                      </span>
                    ) : r.winner === "alt" ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-slate-200 px-2 py-1 text-[11px] font-bold text-slate-700">
                        <X className="h-3 w-3" /> Alt
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-1 text-[11px] font-bold text-amber-700">
                        <Minus className="h-3 w-3" /> เสมอ
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-t border-slate-200 bg-slate-50">
        <div className="mx-auto max-w-5xl px-4 py-12 sm:px-6">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep">คำถามที่พบบ่อย</h2>
          <div className="mt-6 space-y-4">
            {topic.faqs.map((f, i) => (
              <div key={i} className="rounded-xl border border-slate-200 bg-white p-5">
                <h3 className="font-bold text-navy-deep">{f.q}</h3>
                <p className="mt-2 text-sm text-slate-700">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Other comparisons */}
      <section className="mx-auto max-w-5xl px-4 py-12 sm:px-6">
        <h2 className="font-display text-2xl font-extrabold text-navy-deep">เปรียบเทียบหัวข้ออื่น</h2>
        <div className="mt-6 grid gap-3 sm:grid-cols-2">
          {others
            .map((c) => (
              <Link
                key={c.slug}
                to="/compare/$topic"
                params={{ topic: c.slug }}
                className="group flex items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white p-4 transition hover:-translate-y-0.5 hover:border-gold/50 hover:shadow-md"
              >
                <span className="font-semibold text-navy-deep group-hover:text-amber-600">{c.titleTh}</span>
                <ArrowRight className="h-4 w-4 text-slate-400 group-hover:text-amber-600" />
              </Link>
            ))}
        </div>
      </section>

      <CompareTripleKeyLinks mode="th" topicTitleTh={topic.titleTh} topicTitleEn={topic.titleEn} />
      <RelatedServiceLocations comparisonSlug={topic.slug} locale="th" />
      <TrustLayer variant="compact" />
    </main>
  );
}
